import { API_TYPES, post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { type CbhApiErrors, isCbhApiAxiosError } from "@src/appV2/Errors/errorUtils";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

// Will replace it with  contract package from backend when backend merges.
// For now to test and merge stuff in parallel, we use this schema hardcoded here.
const workConfirmationLetterEmailRequestSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.workConfirmationLetterEmail),
    attributes: z.object({
      qualification: z.string(),
      state: z.string(),
    }),
  }),
});

// 202 response has no body, so we accept void, undefined, or empty string
const workConfirmationLetterEmailResponseSchema = z.union([z.void(), z.undefined(), z.literal("")]);

export type WorkConfirmationLetterEmailRequest = z.infer<
  typeof workConfirmationLetterEmailRequestSchema
>;

export type WorkConfirmationLetterEmailResponse = z.infer<
  typeof workConfirmationLetterEmailResponseSchema
>;

function getWorkConfirmationLetterEmailUrl(workerId: string): string {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/work-confirmation-letter-emails`;
}

interface SendWorkConfirmationLetterEmailParams {
  state: string;
  qualification: string;
}

export function useLicenseRenewalWorkDocuments(
  options: UseMutationOptions<
    WorkConfirmationLetterEmailResponse,
    AxiosError<CbhApiErrors>,
    SendWorkConfirmationLetterEmailParams
  > = {}
): UseMutationResult<
  WorkConfirmationLetterEmailResponse,
  AxiosError<CbhApiErrors>,
  SendWorkConfirmationLetterEmailParams
> {
  const worker = useDefinedWorker();
  const { showSuccessToast, showErrorToast } = useToast();

  return useMutation({
    mutationFn: async (params: SendWorkConfirmationLetterEmailParams) => {
      const { qualification, state } = params;

      await post({
        url: getWorkConfirmationLetterEmailUrl(worker.userId),
        data: {
          data: {
            type: API_TYPES.workConfirmationLetterEmail,
            attributes: {
              qualification,
              state,
            },
          },
        },
        requestSchema: workConfirmationLetterEmailRequestSchema,
        responseSchema: workConfirmationLetterEmailResponseSchema,
      });

      // For 202 responses, axios may return empty string, so we return undefined
      return undefined as WorkConfirmationLetterEmailResponse;
    },
    onSuccess: () => {
      showSuccessToast(`We have sent you an email to ${worker.email}`);
    },
    onError: (error, params) => {
      const { qualification, state } = params;

      if (isCbhApiAxiosError(error)) {
        showErrorToast(error.response.data.errors[0].detail);
        logEvent(APP_V2_APP_EVENTS.CANNOT_GENERATE_LICENSE_RENEWAL_DOCS, {
          reason: error.response.data.errors[0].detail,
          qualification,
          state,
        });
        return;
      }

      showErrorToast("Something went wrong while sending work documents email. Please try again.");
      logEvent(APP_V2_APP_EVENTS.CANNOT_GENERATE_LICENSE_RENEWAL_DOCS, {
        reason: "Unknown",
        qualification,
        state,
      });
    },
    ...options,
  });
}
